import {
    Component,
    EventEmitter,
    Output,
    AfterViewInit,
    ViewChild,
    inject,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, distinctUntilChanged, map, Observable } from "rxjs";
import { Store } from "@ngrx/store";
import { RootState } from "../../store/root";
import { ResetStore } from "../../store/reset-meta";
import { ValidityState } from "../../store/validity/state";

import { UtilsComponent } from "../../components/utils.component";
import { SignModal } from "../../components/modals/sign-modal.component";
import { Strings } from "../../app.constants";
import { LocalState } from "../pdf/pdf-viewer.component";
import { Esignature } from "./esignature.component";
import { EsignService } from "./esign.service";
import { SignpadService } from "./signpad.service";
import { PersistenceService } from "../../store/model/persistence.service";
import { ValidationService } from "../../services/validation.service";
import { NotificationService } from "../../services/notification.service";
import { ConsultationProcess } from "../../services/consultation.service";
import { Signature } from "../../store/document/state";
import * as DocActions from "../../store/document/actions";
import { Finalize } from "../../store/document/actions";
import { FinalizingModal } from "../../components/modals/finalizing-modal.component";
import { LoggerService } from "../../services/logger.service";

const signatureScale = 3.8;

@Component({
    selector: "zbd-esign",
    templateUrl: "esignwidget.component.html",
    styles: [
        `
            .waiting,
            .error {
                padding: 1em;
                font-size: 160%;
            }

            .waiting .message {
                vertical-align: middle;
                padding-left: 0.5em;
            }

            .waiting > .spinning {
                vertical-align: middle;
                font-size: 32px;
            }
        `,
    ],
})
export class EsignWidgetComponent
    extends UtilsComponent
    implements AfterViewInit
{
    @ViewChild(SignModal) signModal: SignModal;
    @ViewChild(FinalizingModal) finalizingModal: FinalizingModal;
    buildVersion = version.BUILD_VERSION;

    validity$: Observable<ValidityState>;

    state$: Observable<LocalState>;
    cp: ConsultationProcess;

    currentField: Signature;
    signatureScale: number = signatureScale;

    showWaiting = false;
    waitMessage: string;

    private logger = inject(LoggerService);

    constructor(
        private esignService: EsignService,
        private signpad: SignpadService,
        private validation: ValidationService,
        private persistence: PersistenceService,
        private notificationService: NotificationService,
        private store: Store<RootState>,
        private route: ActivatedRoute,
        private router: Router,
    ) {
        super();

        this.store.dispatch(new ResetStore());
        this.state$ = LocalState.create(store, esignService.esignView$);
        this.validity$ = store.select<ValidityState>((s) => s.validity);

        this.state$.subscribe((state) => {
            this.cp = state.cp;
        });

        this.register(
            this.route.params.subscribe((params) => {
                const cpId: number = parseInt(params["protocolID"]);
                this.logger.log(
                    "Loading consultation process with ID %d.",
                    cpId,
                );
                this.persistence.loadConsultationProcess(cpId);
            }),
        );
    }

    ngAfterViewInit(): void {
        this.register(
            this.state$
                .pipe(
                    map((s) => s.document.dirty),
                    distinctUntilChanged(),
                )
                .subscribe((isDirty) => {
                    if (isDirty) {
                        this.closeModals();
                        this.store.dispatch(new DocActions.Build());
                    }
                }),
        );
        this.register(
            this.state$.pipe(map((s) => s.document)).subscribe((docState) => {
                if (
                    !this.currentField &&
                    docState.signatures &&
                    docState.signatures.length > 0
                ) {
                    this.currentField = docState.signatures[0];
                }
            }),
        );
    }

    public closeModals(): void {
        this.finalizingModal.close();
    }

    public finalizeProtocol(cp: ConsultationProcess) {
        this.closeModals();
        this.finalizingModal.open();
        this.store.dispatch(new DocActions.Finalize());
    }

    public trackSignature(index: number, sig: Signature) {
        return sig.index;
    }
}
